import { IBaseDocument, IBaseDocumentCreate } from './base'
import { DriveDocument } from './drive'
import { WorkstationDocument } from './workstation'
export type Currency = 'usd'

export type UserTaxType = 'individual' | 'business'

// Firebase collection object ID (user ID)
// Users collection is tied to authentication in firebase so
// we are not generating uuids for users. Instead, we use
// Firebase user identifiers.
export type UserIdentifier = string

export type TaxRates = {
  [K in UserTaxType]: string
}

export type BugReport = {
  category: string
  description: string
  expectedResult?: string
  additionalDetails?: string
  userAgent: string
}

export type ContactSupport = {
  category: string
  description: string
}

export type Consents = {
  data_processing: boolean
  terms_and_conditions: boolean
  terms_of_service: boolean
  privacy_policy: boolean
}

export type Limits = {
  max_number_of_workstations: number
  max_number_of_drives: number
}

export type StoragePlan = {
  created: number
  trial_end: number | null
}

export type Credits = {
  amount: number
  total: number
}

export type AwsRegion = string

export type AuthProvider = 'google.com' | 'password'

export type UserStatus =
  | 'active'
  | 'blocked_by_admin'
  | 'missing_payment'
  | 'scheduled_for_deletion'
// active or no status:
// user can use Renderro normally.
// blocked_by_admin - user was blocked due to a strange behavior
// missing_payment - before starting or creating resources user has to pay for negative balance.
// scheduled_for_deletion - user was not paying for the last 14 days. We can remove all user resources.

export interface UserDocumentCreate extends IBaseDocumentCreate {
  account_suspended_timestamp?: number | null
  billing_is_enabled?: boolean // by default it is enabled for production users, this flag controls whether is should also be enabled for dev
  billing?: Billing | null
  consents?: Consents
  currency?: Currency // TODO
  first_name: string
  is_renderro_admin: boolean
  never_block_account?: boolean
  last_name: string
  limits?: Limits
  missing_payments_total?: number
  region?: AwsRegion
  selected_tier?: SelectedStorageTier | null
  should_get_notified_about_teams_feature?: boolean
  status?: UserStatus
  storage_plan?: StoragePlan | null
  user_tax_type?: UserTaxType
  workstation_notify_if_idling: boolean
  workstation_turn_off_idling: boolean
  workstation_turn_off_on_disconnect: boolean
  extra_workstation_codenames: string[]
}

// Firebase document: /users/{user_id}
export interface UserDocument extends UserDocumentCreate, IBaseDocument {}

// Merges information about user from both Authentication in Firebase
// and user profile set on /users/{user_id} document
export class UserProxy {
  constructor(
    public uid: string,
    public profile: UserDocument,
    public email: string,
    public displayName: string | null,
    public photoURL: string,
    public provider: AuthProvider,
    public workstations: Array<WorkstationDocument>,
    public drives: Array<DriveDocument>,
  ) {}
  get finalDisplayName(): string {
    const fullName = [this.profile.first_name, this.profile.last_name].join(' ').trim()
    return fullName || this.displayName || (this.email ? this.email.split('@')[0] : '')
  }
  get runningWorkstations(): Array<WorkstationDocument> {
    return this.workstations.filter((w) => w.status === 'running')
  }
  get maxNumberOfWorkstations(): number {
    if (this.profile.limits) {
      return this.profile.limits.max_number_of_workstations
    }
    return this.workstations.length
  }
  get maxNumberOfDrives(): number {
    if (this.profile.limits) {
      return this.profile.limits.max_number_of_drives
    }
    return this.drives.length
  }
  get taxType(): UserTaxType {
    // some users might lack the field, hence the default:
    return this.profile.user_tax_type || 'individual'
  }
}

export interface Billing {
  address_line_1: string
  address_line_2: string
  card: CardSummary
  city: string
  country: string
  full_name: string
  postal_code: string
  stripe_active_payment_method_identifier: string
  stripe_customer_identifier: string
  affiliate_hash?: string
}

export interface CardSummary {
  cardholder_name: string
  last_4_digits: string
  exp_month: string
  exp_year: string
  // Existing customers won't have these fields
  // We have to add a migration and remove ?
  brand?: Brand
  funding?: Funding
}

export type Funding = 'credit' | 'debit' | 'prepaid' | 'unknown'

export type Brand =
  | 'amex'
  | 'cartes_bancaires'
  | 'diners_club'
  | 'discover'
  | 'jcb'
  | 'mastercard'
  | 'visa'
  | 'unionpay'
  | 'unknown'

export type StorageTierCodename =
  | 'standard-200gb-2020-08'
  | 'standard-500gb-2020-08'
  | 'standard-1000gb-2020-08'
  | 'standard-1000gb-plus-2020-08'
  | 'business-1000gb-plus-2020-08'
  | 'renderro-pro-bundle-test-2021-07'
  | 'freelancer-2021-07'
  | 'pro-2021-07'
  | 'studio-2021-07'
  | 'enterprise'

export type BundlePeriod = 'month' | 'year'

export type SelectedStorageTier = {
  codename: StorageTierCodename
  custom_size_gbs: number | null
  bundle_period?: BundlePeriod
}
